import React, { FC, useState } from "react";
import { Table, Button, Popconfirm, Checkbox } from "antd";
import type { ColumnType } from "antd/lib/table";
import { NotificationRule } from "../../data-types";
import { compareAsc } from "date-fns";
import { NotificationRuleRecordType } from "./notification-rule-record-type";

import styles from "./notification-rules-table.css";

const {
  ["button"]: buttonClassName,
  ["top-row"]: topRowClassName,
  ["popconfirm-okay"]: popconfirmOkayClassName,
} = styles;

type Props = {
  loading: boolean;
  notificationRules: NotificationRule[];
  onDeleteNotificationRules: (ids: number[]) => void;
  onUpdateActiveNotificationRules: (ids: number[], active: boolean) => void;
  onClickNotificationRule: (id: number) => void;
  onClickCreateNotificationButton: () => void;
};

export const NotificationRulesTable: FC<Props> = ({
  loading,
  notificationRules,
  onDeleteNotificationRules,
  onUpdateActiveNotificationRules,
  onClickNotificationRule,
  onClickCreateNotificationButton,
}) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: NotificationRuleRecordType[]) => {
      setSelectedRows(selectedRows.map((record) => record.id));
    },
  };

  const handleDelete = () => {
    onDeleteNotificationRules(selectedRows);
    setSelectedRows([]);
  };

  const tableColumns: ColumnType<NotificationRuleRecordType>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (value: string) => new Date(value).toLocaleString(),
      sorter: (a, b) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (value: boolean, record: NotificationRuleRecordType) => (
        <Checkbox
          checked={value}
          onClick={(e) => {
            e.stopPropagation();
            if (selectedRows.length === 0 || !selectedRows.includes(record.id)) {
              onUpdateActiveNotificationRules([record.id], !value);
            } else {
              onUpdateActiveNotificationRules(selectedRows, !value);
            }
          }}
        />
      ),
    },
  ];

  return (
    <>
      <div className={topRowClassName}>
        <Popconfirm
          okButtonProps={{ className: popconfirmOkayClassName }}
          placement="left"
          title="Are you sure you want to delete these notifications?"
          disabled={selectedRows.length === 0}
          onConfirm={(e) => {
            if (e) {
              e.stopPropagation();
              handleDelete();
            }
          }}
          onCancel={(e) => {
            if (e) {
              e.stopPropagation();
            }
          }}
          okText="Yes"
          cancelText="No"
        >
          <Button
            className={buttonClassName}
            type="primary"
            danger
            onClick={(e) => {
              e.stopPropagation();
            }}
            disabled={selectedRows.length === 0}
          >
            Delete selected
          </Button>
        </Popconfirm>
        <Button
          className={buttonClassName}
          type="primary"
          onClick={onClickCreateNotificationButton}
        >
          Create Alert
        </Button>
      </div>
      <Table
        loading={loading}
        dataSource={notificationRules}
        columns={tableColumns}
        onRow={(record) => {
          return {
            onClick: () => onClickNotificationRule(record.id),
          };
        }}
        rowKey={(record) => record.id}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
      />
    </>
  );
};
